import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Typography,
  Fade,
  styled
} from '@mui/material';
import PropTypes from 'prop-types';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2)
  },
  '& .MuiDialogTitle-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2, 2, 3)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(0, 2, 2)
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  minWidth: 100,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 3),
  transition: theme.transitions.create(['background-color', 'box-shadow', 'transform'], {
    duration: theme.transitions.duration.short
  }),
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 8px ${theme.palette.mode === 'dark'
      ? 'rgba(0, 0, 0, 0.2)'
      : 'rgba(15, 23, 42, 0.1)'}`
  },
  '&:active': {
    transform: 'translateY(0)',
    boxShadow: `0 2px 4px ${theme.palette.mode === 'dark'
      ? 'rgba(0, 0, 0, 0.15)'
      : 'rgba(15, 23, 42, 0.08)'}`
  }
}));

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor = 'primary',
  onConfirm,
  onCancel,
  isLoading = false
}) {
  return (
    <StyledDialog
      open={open}
      onClose={onCancel}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
      sx={{
        '& .MuiDialog-paper': {
          padding: 1
        }
      }}
    >
      <DialogTitle id="confirm-dialog-title">
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <DialogContentText
          id="confirm-dialog-description"
          sx={{
            color: 'text.primary',
            fontSize: '1rem',
            lineHeight: 1.6
          }}
        >
          {message}
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ gap: 1 }}>
        <StyledButton
          onClick={onCancel}
          color="inherit"
          disabled={isLoading}
          variant="outlined"
          sx={{
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'text.primary',
              backgroundColor: 'action.hover'
            }
          }}
        >
          {cancelText}
        </StyledButton>
        <StyledButton
          onClick={onConfirm}
          color={confirmColor}
          variant="contained"
          autoFocus
          disabled={isLoading}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            {isLoading && (
              <CircularProgress
                size={16}
                thickness={4}
                color="inherit"
                sx={{ mr: 0.5 }}
              />
            )}
            {confirmText}
          </Box>
        </StyledButton>
      </DialogActions>
    </StyledDialog>
  );
}

ConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  confirmColor: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
};
